import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Avatar,
  Box,
  CircularProgress,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Popover,
  TextField,
  Typography,
} from "@mui/material";
import ImageIcon from "@mui/icons-material/Image";
import SearchIcon from "@mui/icons-material/Search";
import { API_ENDPOINT } from "../../utils/constants"; // Your existing import
import { getHeader } from "../../utils/util";

const DEBOUNCE_MS = 200;
const ReusableImageSelector = ({ open, anchorEl, onClose, onSelect, url }) => {
  // Data & UI state
  const [images, setImages] = useState([]); // [{ url, fileName }]
  const [loading, setLoading] = useState(false);

  // Search state
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");

  /* ------------------------ Fetch images when popover opens ----------------- */
  useEffect(() => {
    if (!open) return; // Skip if already have data
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(url, {
          method: "GET",
          ...getHeader(),
        });
        const raw = await res.json();
        const list = Array.isArray(raw) ? raw : raw.data || [];

        // Normalize fields to decouple from backend shape
        const normalized = list.map(
          ({ preview_url, header_handle, mediaId, file_name }) => ({
            url: preview_url,
            fileName: file_name,
            mediaId: mediaId,
            asset: header_handle,
          })
        );

        setImages(normalized);
      } catch (err) {
        console.error("Fetch images failed:", err);
        setImages([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  /* --------------------- Debounce the search string ------------------------ */
  useEffect(() => {
    const id = setTimeout(
      () => setDebounced(query.trim().toLowerCase()),
      DEBOUNCE_MS
    );
    return () => clearTimeout(id);
  }, [query]);

  /* ----------------------------- Filtered list ----------------------------- */
  const filtered = useMemo(() => {
    if (!debounced) return images;
    return images.filter((img) =>
      img.fileName.toLowerCase().includes(debounced)
    );
  }, [images, debounced]);

  /* -------------------------- Select an image ----------------------------- */
  const handleSelect = useCallback(
    (img) => {
      onSelect(img); // Call parent callback with selected image
      onClose(); // Close popover
    },
    [onSelect, onClose]
  );

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      PaperProps={{ sx: { width: 320, maxHeight: 360 } }}
    >
      <Box sx={{ p: 2 }}>
        {/* Search */}
        <TextField
          size="small"
          fullWidth
          placeholder="Search filename…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {/* List or loader */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <List sx={{ mt: 1, maxHeight: 260, overflowY: "auto" }}>
            {filtered.length === 0 && (
              <Typography
                variant="body2"
                sx={{ p: 2, color: "text.secondary" }}
              >
                No matches
              </Typography>
            )}

            {filtered.map((img) => (
              <ListItem
                key={img.url}
                button
                onClick={() => handleSelect(img)}
                sx={{ py: 0.75 }}
              >
                <ListItemAvatar>
                  <Avatar
                    variant="square"
                    src={img.url}
                    alt={img.fileName}
                    sx={{ width: 48, height: 48, mr: 1 }}
                  >
                    <ImageIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText primary={img.fileName} />
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </Popover>
  );
};

export default ReusableImageSelector;
